import { ActionIcon, Box, createStyles, Group, Image, Text, Tooltip } from '@mantine/core';
import { IconX } from '@tabler/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import FileUpload from '../../../Organismns/FileUpload';
import { ConfirmDialog, useModalState } from '../../../Molecules/Dialogs';
import PanePaper from '../../../Molecules/PanePaper';
import { PdfS } from '../../../Service/PdfS';
import { ConstantS } from '../../../Service/ConstantS';
import { MixpanelS } from '../../../Service/MixpanelS';

const useStyles = createStyles((theme) => ({
  compactCard: {
    backgroundColor: 'transparent',
  },
  preview: {
    overflow: 'hidden',
    borderRadius: theme.radius.md,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
    backgroundImage:
      theme.colorScheme === 'dark'
        ? 'none'
        : 'linear-gradient(45deg, #f6f7f9 25%, transparent 25%), linear-gradient(-45deg, #f6f7f9 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f6f7f9 75%), linear-gradient(-45deg, transparent 75%, #f6f7f9 75%)',
    backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
    backgroundSize: '12px 12px',
  },
}));

export const MediaUpload = ({
  title,
  text,
  kind,
  lastModified,
  setLastModified,
  setLogoUploaded,
  showPreview,
  compact = false,
}) => {
  const { classes } = useStyles();
  const [available, setAvailable] = useState(false);
  const { show, toggle } = useModalState(false);
  const [blob, setBlob] = useState();
  const previewUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : undefined), [blob]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const lookUpAvailability = useCallback(() => {
    fetch(`${process.env.REACT_APP_PDF_SERVICE_URL}/layout/media/${kind}`, {
      method: 'HEAD',
      credentials: 'include',
    })
      .then((response) => response.headers)
      .then((headers) => {
        const mediaAvailable = parseInt(headers.get('X-Total-Count'), 10) === 1;
        setAvailable(mediaAvailable);
        if (kind === 'logo') {
          setLogoUploaded(mediaAvailable);
        }
      })
      .catch((err) => {
        console.error(`fetch error${err}`);
      });
  }, [kind, setLogoUploaded]);

  useEffect(() => {
    lookUpAvailability();
  }, [lastModified, lookUpAvailability]);

  useEffect(() => {
    let active = true;

    if ((showPreview || compact) && available) {
      PdfS.getLayoutMedia(kind).then((_blob) => {
        if (active) {
          setBlob(_blob || undefined);
        }
      });
    } else if (!available) {
      setBlob(undefined);
    }

    return () => {
      active = false;
    };
  }, [showPreview, compact, available, kind, lastModified]);

  function deleteMedia() {
    if (kind !== null) {
      fetch(`${process.env.REACT_APP_PDF_SERVICE_URL}/layout/media/${kind}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      }).then((response) => {
        toggle();
        if (response.ok) {
          setAvailable(false);
          setBlob(undefined);
          if (kind === 'logo') {
            setLogoUploaded(false);
          }
          setLastModified(new Date().toString());
        } else {
          lookUpAvailability();
        }
      });
    }
  }

  function reload() {
    lookUpAvailability();
    setLastModified(new Date().toString());
  }

  const compactUploadText = available ? 'Datei ersetzen' : 'Datei auswählen oder ablegen';

  const uploader = (
    <FileUpload
      compact={compact}
      kind="notepaper"
      reloadCallback={() => {
        MixpanelS.track(ConstantS.TrackingEvents.LogoUploaded);
        reload();
      }}
      serverUrl={`${process.env.REACT_APP_PDF_SERVICE_URL}/layout/media/${kind}`}
      text={compact ? compactUploadText : text}
    />
  );

  const dialog = (
    <ConfirmDialog
      text={`layout.${kind}.delete.confirm`}
      subject={`layout.${kind}.delete.confirm.head`}
      open={show}
      toggle={toggle}
      handler={deleteMedia}
    />
  );

  if (compact) {
    return (
      <>
        <Box p="xs" className={classes.compactCard}>
          <Box sx={{ minWidth: 0 }}>
            <Group position="apart" noWrap spacing="xs">
              <Text size="sm" weight={700} lineClamp={1}>
                {title}
              </Text>
              <Group noWrap spacing={4}>
                {available && (
                  <Tooltip label="Datei entfernen" withArrow>
                    <ActionIcon
                      size="sm"
                      color="red"
                      variant="subtle"
                      onClick={toggle}
                      aria-label={`${title} entfernen`}
                    >
                      <IconX size={15} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Group>
            <Text size="xs" color="dimmed" mt={1} mb={6}>
              {text}
            </Text>
            {previewUrl && (
              <Box className={classes.preview} mb={6}>
                <Image
                  height={64}
                  width="100%"
                  fit="contain"
                  src={previewUrl}
                  alt=""
                  onError={() => setBlob(undefined)}
                />
              </Box>
            )}
            {uploader}
          </Box>
        </Box>
        {dialog}
      </>
    );
  }

  return (
    <>
      <PanePaper title={title} rightSection={available && <IconX onClick={toggle} style={{ cursor: 'pointer' }} />}>
        {(!blob || !showPreview) && uploader}
        {previewUrl && (
          <Image height={150} width="100%" fit="contain" src={previewUrl} alt="" onError={() => setBlob(undefined)} />
        )}
      </PanePaper>
      {dialog}
    </>
  );
};
